import type { AppPreferencesRecord, BackupPreferences } from "@app/core";
import { getServices } from "../platform.js";
import { useSettingsStore } from "../stores/settings.store.js";
import {
  APP_PREFERENCES_ID,
  PREFERENCES_CREATED_AT_KEY,
  PREFERENCES_UPDATED_AT_KEY,
  setPreferencesSyncClock,
} from "./preferences.js";

/** Curated, secret-free preferences to embed in backups and sync. */
export function gatherPreferences(): BackupPreferences {
  const s = useSettingsStore.getState();
  return {
    shortcuts: s.shortcuts,
    sidebarFilterOrder: s.sidebarFilterOrder,
    sidebarNavOrder: s.sidebarNavOrder,
    sidebarVisible: s.sidebarVisible,
    prioritiesNavVisibleOverride: s.prioritiesNavVisibleOverride,
    sidebarCollapsed: s.sidebarCollapsed,
    topicOrder: s.topicOrder,
    topicChildOrder: s.topicChildOrder,
    labelOrder: s.labelOrder,
    calendarOrder: s.calendarOrder,
    monthExcludedTopicIds: s.monthExcludedTopicIds,
    monthExcludedCalendarIds: s.monthExcludedCalendarIds,
    todayExcludedTopicIds: s.todayExcludedTopicIds,
    todayExcludedCalendarIds: s.todayExcludedCalendarIds,
    prioritiesExcludedTopicIds: s.prioritiesExcludedTopicIds,
    weekStartsOn: s.weekStartsOn,
    multiDayFocus: s.multiDayFocus,
    defaultTz: s.defaultTz,
    defaultCalendarId: s.defaultCalendarId,
    timeFormat: s.timeFormat,
    defaultView: s.defaultView,
    defaultReminderMinutes: s.defaultReminderMinutes,
    defaultEventReminderMinutes: s.defaultEventReminderMinutes,
    defaultEntryIsEvent: s.defaultEntryIsEvent,
    defaultDurationMin: s.defaultDurationMin,
    snoozeMorningHour: s.snoozeMorningHour,
    snoozeEveningHour: s.snoozeEveningHour,
    snoozeAfternoonHour: s.snoozeAfternoonHour,
    agendaDays: s.agendaDays,
    agendaShowEmptyDays: s.agendaShowEmptyDays,
    theme: s.theme,
    language: s.language,
    textScale: s.textScale,
    textFont: s.textFont,
    lastCalendarView: s.lastCalendarView,
    subtaskRollup: s.subtaskRollup,
    matrixScope: s.matrixScope,
    deadlineUrgency: s.deadlineUrgency,
    deadlineUrgencyHighDays: s.deadlineUrgencyHighDays,
    deadlineUrgencyMediumDays: s.deadlineUrgencyMediumDays,
    calendarHourHeight: s.calendarHourHeight,
    calendarDayCount: s.calendarDayCount,
    blockTintDiff: s.blockTintDiff,
    blockGradient: s.blockGradient,
    sidebarWidth: s.sidebarWidth,
    sidebarOpen: s.sidebarOpen,
    mobileTopicDisplay: s.mobileTopicDisplay,
    mobileTopicMulti: s.mobileTopicMulti,
    desktopFormVariant: s.desktopFormVariant,
    mobileSheetHeight: s.mobileSheetHeight,
    mobileScreenshotFeedback: s.mobileScreenshotFeedback,
    showCalendarSwipeHint: s.showCalendarSwipeHint,
    onboardingComplete: s.onboardingComplete,
    onboardingStep: s.onboardingStep,
  };
}

export async function gatherPreferencesSyncRecords(): Promise<AppPreferencesRecord[]> {
  const record = await currentPreferencesSyncRecord();
  return record ? [record] : [];
}

export async function currentPreferencesSyncRecord(): Promise<AppPreferencesRecord | null> {
  const { settings } = getServices();
  const updatedAt = await settings.get(PREFERENCES_UPDATED_AT_KEY);
  if (!updatedAt) return null;
  const createdAt = (await settings.get(PREFERENCES_CREATED_AT_KEY)) ?? updatedAt;
  return {
    id: APP_PREFERENCES_ID,
    created_at: createdAt,
    updated_at: updatedAt,
    deleted_at: null,
    ...gatherPreferences(),
  };
}

export async function applyPreferencesSyncRecords(rows: AppPreferencesRecord[]): Promise<void> {
  const record = rows
    .filter((row) => row.id === APP_PREFERENCES_ID && !row.deleted_at)
    .sort((a, b) => (a.updated_at < b.updated_at ? 1 : a.updated_at > b.updated_at ? -1 : 0))[0];
  if (!record) return;
  await useSettingsStore.getState().applyBackupPreferences(record, { touch: false });
  await setPreferencesSyncClock(record.created_at, record.updated_at);
}
